import type {Nodes as MarkdownNode} from 'mdast'
import {Node} from 'prosemirror-model'
import {Plugin} from 'prosemirror-state'
import {Decoration, type DecorationAttrs, DecorationSet} from 'prosemirror-view'
import {visit} from 'unist-util-visit'

import {getNodeSource, isPositionedNode, parseToMdast} from '../schema/utils'
import styles from './hybrid-block.module.css'

const inlineNodeElements = {
  strong: 'strong',
  emphasis: 'em',
  inlineCode: 'code',
  delete: 'del',
  link: 'a',
} as const satisfies Partial<Record<MarkdownNode['type'], keyof HTMLElementTagNameMap>>

type InlineNode = MarkdownNode & {type: keyof typeof inlineNodeElements}

function isInlineNode(node: MarkdownNode): node is InlineNode {
  return node.type in inlineNodeElements
}

/**
 * Determines the start and end delimiters of a Markdown node by determining where in the node the actual 'content' is,
 * and treating everything that comes before or after that as a delimiter. Then applies decorations to the delimiters
 * and content. The delimiters always get the same decoration, while the content decoration is defined by `attrs`.
 *
 * This approach will work for any Markdown syntax that has purely surrounding delimiters. So it will work on headings
 * and inline formatting, but not on lists or blockquotes. Thankfully, those aren't hybrid nodes anyway.
 *
 * TODO: ideally, we would rather define the delimiter shape on the nodes themselves as part of the Markdown parsing
 * step where we are already tokenizing the content. Maybe we can do that as a Remark plugin?
 */
function createHybridNodeDecorations(
  parentText: string,
  parentPos: number,
  mdNode: MarkdownNode,
  attrs: DecorationAttrs,
) {
  if (!isPositionedNode(mdNode)) return []

  const start = parentPos + mdNode.position.start.offset
  const end = parentPos + mdNode.position.end.offset
  const nodeSource = getNodeSource(mdNode, parentText)
  if (!nodeSource) return []

  const valueSource =
    'value' in mdNode && typeof mdNode.value === 'string'
      ? mdNode.value
      : 'children' in mdNode
        ? mdNode.children.map(child => getNodeSource(child, parentText)).join('')
        : ''
  const valueIndex = nodeSource.indexOf(valueSource)

  const startDelimiter = nodeSource.slice(0, valueIndex)
  const endDelimiter = nodeSource.slice(valueIndex + valueSource.length)

  // There seems to be a ProseMirror bug when the end delimiter starts with a newline; if we don't offset the positions,
  // it eats the last character of the text and exposes the last character of the delimiter. We might also run into similar
  // bugs when the start delimiter ends with a newline, but we'll figure that out when we get there.
  const endDelimiterOffset = endDelimiter.startsWith('\n') ? 1 : 0

  const decorations = [
    Decoration.inline(start, start + startDelimiter.length, {
      nodeName: 'span',
      class: styles.delimiter,
    }),
    Decoration.inline(start + startDelimiter.length, end - endDelimiter.length, attrs),
    Decoration.inline(end - endDelimiter.length + endDelimiterOffset, end + endDelimiterOffset, {
      nodeName: 'span',
      class: styles.delimiter,
    }),
  ]

  return decorations
}

/**
 * In addition to decorating the surrounding delimiters and content like `createHybridNodeDecorations`, this also applies
 * a node decoration to the node itself, wrapping the whole shebang in an element.
 */
function createFlowNodeDecorations(parentNode: Node, parentPos: number, mdNode: MarkdownNode, attrs: DecorationAttrs) {
  return [
    Decoration.node(parentPos, parentPos + parentNode.nodeSize, attrs),
    ...createHybridNodeDecorations(parentNode.textContent, parentPos, mdNode, {}),
  ]
}

const decorateHybridMarkdown = (doc: Node) => {
  const decorations: Decoration[] = []
  doc.descendants((node, pos) => {
    if (node.type.name === 'paragraph') {
      const value = node.textContent
      const tree = parseToMdast(value)

      // Wrap in the correct outer node
      const firstChild = tree.children[0]
      // TODO: We'll probably want to make this more generic like we do for inline nodes
      if (tree.children.length === 1 && firstChild?.type === 'heading') {
        decorations.push(
          ...createFlowNodeDecorations(node, pos, firstChild, {
            nodeName: `h${firstChild.depth}`,
          }),
        )
      } else {
        decorations.push(Decoration.node(pos, pos + node.nodeSize, {nodeName: 'p'}))
      }

      // Style any inline formatting in the content
      visit(tree, mdNode => {
        if (isInlineNode(mdNode))
          decorations.push(
            ...createHybridNodeDecorations(value, pos + 1, mdNode, {
              nodeName: inlineNodeElements[mdNode.type],
              href: mdNode.type === 'link' ? mdNode.url : undefined,
            }),
          )
      })
    }
  })
  return DecorationSet.create(doc, decorations)
}

export const decorateHybridMarkdownPlugin = () =>
  new Plugin({
    state: {
      init(_, {doc}) {
        return decorateHybridMarkdown(doc)
      },
      apply(tr, oldDecorations) {
        if (tr.docChanged) {
          return decorateHybridMarkdown(tr.doc)
        }
        return oldDecorations
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
    },
  })
